<template>
    <div
        v-editable="blok"
        class="contact-form"
    >
        <Transition
            name="fade"
            mode="out-in"
        >
            <div
                v-if="!savingSuccessful"
                key="contact-form"
                class="contact-form__inner"
            >
                <h3 class="contact-form__heading">
                    Get in touch with us
                </h3>
                <form
                    ref="form-element"
                    class="contact-form__form"
                    novalidate
                    @submit.prevent="submitForm"
                    @focusin.once="trackStart"
                >
                    <label
                        class="k-visually-hidden"
                        aria-hidden="true"
                    >
                        Website
                        <input
                            v-model="website"
                            name="website"
                            tabindex="-1"
                            autocomplete="off"
                        >
                    </label>
                    <form.Field
                        v-slot="{ field }"
                        name="fullname"
                        :validators="{
                            onDynamic: ({ value }) => requiredText(value, 'Enter your full name.'),
                        }"
                    >
                        <KField
                            label="Full name"
                            required
                            :error="firstError(field.state.meta.errors)"
                        >
                            <KInput
                                type="text"
                                name="fullname"
                                autocomplete="name"
                                :model-value="field.state.value"
                                @update:model-value="field.handleChange"
                                @blur="field.handleBlur"
                            />
                        </KField>
                    </form.Field>
                    <form.Field
                        v-slot="{ field }"
                        name="email"
                        :validators="{ onDynamic: ({ value }) => validEmail(value) }"
                    >
                        <KField
                            label="E-mail"
                            required
                            :error="firstError(field.state.meta.errors)"
                        >
                            <KInput
                                type="email"
                                name="email"
                                autocomplete="email"
                                :model-value="field.state.value"
                                @update:model-value="field.handleChange"
                                @blur="field.handleBlur"
                            />
                        </KField>
                    </form.Field>

                    <form.Field
                        v-slot="{ field }"
                        name="message"
                        :validators="{
                            onDynamic: ({ value }) => requiredText(value, 'Tell us what we can help you with.'),
                        }"
                    >
                        <KField
                            label="Message"
                            required
                            :error="firstError(field.state.meta.errors)"
                        >
                            <KTextarea
                                name="message"
                                placeholder="Tell us what we can help you with"
                                :rows="5"
                                :model-value="field.state.value"
                                @update:model-value="field.handleChange"
                                @blur="field.handleBlur"
                            />
                        </KField>
                    </form.Field>

                    <form.Field
                        v-slot="{ field }"
                        name="consent"
                        :validators="{
                            onDynamic: ({ value }) => value
                                ? undefined
                                : 'Confirm you have read and agree to the Privacy Policy.',
                        }"
                    >
                        <KField :error="firstError(field.state.meta.errors)">
                            <KCheckbox
                                name="consent"
                                :model-value="field.state.value"
                                @update:model-value="value => field.handleChange(value === true)"
                                @blur="field.handleBlur"
                            >
                                By submitting this form, I confirm that I
                                understand, and agree to the
                                <a href="/privacy-policy">Privacy Policy</a>
                            </KCheckbox>
                        </KField>
                    </form.Field>

                    <KButton
                        type="submit"
                        variant="primary"
                        full-width
                        :disabled="isSubmitting"
                        :label="buttonLabel"
                    />
                </form>
            </div>
            <div
                v-else
                key="success-message"
                class="contact-form__success-message"
            >
                <SuccessMessage />
            </div>
        </Transition>
        <div class="contact-form__side">
            <img
                :src="blok.image?.filename || '/images/contact-us/contact-us-image.png'"
                :alt="blok.image?.alt || 'Contact us image'"
                class="contact-form__side-image"
            >
        </div>
    </div>
</template>

<script setup>
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { KButton, KCheckbox, KField, KInput, KTextarea } from '@vemedia/karibu'
import { computed, nextTick, ref, useTemplateRef } from 'vue'
import { buildContactRequest, EMPTY_CONTACT_FORM } from '../../components/contact-form/contact-form-contract'
import { requiredText, validEmail } from '../../utils/form-validation'
import { sendRequest } from '../../utils/forms'
import SuccessMessage from './success-message.vue'
import './contact-form.scss'

defineProps({
    blok: {
        type: Object,
        required: true,
    },
})

const { capture } = useTracking()

const savingSuccessful = ref(false)
const submitError = ref(false)
const website = ref('')
const formElement = useTemplateRef('form-element')

const form = useForm({
    defaultValues: { ...EMPTY_CONTACT_FORM },
    validationLogic: revalidateLogic({
        mode: 'blur',
        modeAfterSubmission: 'change',
    }),
    onSubmitInvalid: () => {
        nextTick(() => {
            formElement.value
                ?.querySelector('[aria-invalid="true"]')
                ?.focus()
        })
    },
    onSubmit: async ({ value }) => {
        await sendRequest(buildContactRequest(value, window.location.href, website.value))

        savingSuccessful.value = true
        capture('lead_submitted', { form: 'contact-form' })
    },
})

const isSubmitting = form.useSelector(state => state.isSubmitting)
const buttonLabel = computed(() => {
    if (isSubmitting.value) {
        return 'Sending...'
    }

    return submitError.value ? 'Something went wrong' : 'Send Message'
})

function trackStart() {
    capture('lead_started', { form: 'contact-form' })
}

function firstError(errors) {
    return errors.find(error => typeof error === 'string')
}

async function submitForm() {
    if (isSubmitting.value) {
        return
    }

    submitError.value = false

    try {
        // Resolves without calling onSubmit when validation fails; success is
        // flagged inside onSubmit so an invalid submit never shows the success state.
        await form.handleSubmit()
    } catch {
        submitError.value = true
        capture('lead_submit_error', { form: 'contact-form' })
    }
}
</script>
